import { useEffect, useRef, useState } from "react";
import "./MapSection.css";

const parseCoords = (mapsUrl) => {
  if (!mapsUrl) return null;
  const lng = mapsUrl.match(/!2d(-?\d+\.\d+)/);
  const lat = mapsUrl.match(/!3d(-?\d+\.\d+)/);
  if (lat && lng) return { lat: parseFloat(lat[1]), lng: parseFloat(lng[1]) };
  return null;
};

const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const HOTEL_COLOR = "#6366f1";
const CAFE_COLOR = "#10b981";

// Iconic pin design with hotel/coffee icons
const createIconicPin = (type) => {
  const color = type === "hotel" ? HOTEL_COLOR : CAFE_COLOR;

  const icon =
    type === "hotel"
      ? `
        <!-- Building -->
        <path
          d="M5 3H19C19.5523 3 20 3.44772 20 4V21H4V4C4 3.44772 4.44772 3 5 3Z"
          fill="none"
          stroke="white"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />

        <!-- Windows -->
        <rect x="7" y="6" width="2" height="2" rx="0.4" fill="white" />
        <rect x="11" y="6" width="2" height="2" rx="0.4" fill="white" />
        <rect x="15" y="6" width="2" height="2" rx="0.4" fill="white" />

        <rect x="7" y="10" width="2" height="2" rx="0.4" fill="white" />
        <rect x="11" y="10" width="2" height="2" rx="0.4" fill="white" />
        <rect x="15" y="10" width="2" height="2" rx="0.4" fill="white" />

        <!-- Door -->
        <path
          d="M10 21V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V21"
          fill="none"
          stroke="white"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      `
      : `
        <path d="M18 8h1a4 4 0 0 1 0 8h-1" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" fill="none" stroke="white" stroke-width="1.5" stroke-linejoin="round"/>
        <line x1="6" y1="1" x2="6" y2="4" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
        <line x1="10" y1="1" x2="10" y2="4" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
        <line x1="14" y1="1" x2="14" y2="4" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
      `;

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <path
        d="M18 0C10.3 0 4 6.3 4 14c0 10 14 30 14 30s14-20 14-30c0-7.7-6.3-14-14-14z"
        fill="${color}"
        stroke="white"
        stroke-width="1.5"
      />

      <g transform="translate(18,14) scale(0.7) translate(-12,-12)">
        ${icon}
      </g>
    </svg>
  `;
};

// SVG Icons as React components
const HotelIcon = ({ size = 18, color = "#94a3b8" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Building */}
    <path
      d="M5 3H19C19.5523 3 20 3.44772 20 4V21H4V4C4 3.44772 4.44772 3 5 3Z"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Windows */}
    <rect x="7" y="6" width="2" height="2" rx="0.4" fill={color} />
    <rect x="11" y="6" width="2" height="2" rx="0.4" fill={color} />
    <rect x="15" y="6" width="2" height="2" rx="0.4" fill={color} />

    <rect x="7" y="10" width="2" height="2" rx="0.4" fill={color} />
    <rect x="11" y="10" width="2" height="2" rx="0.4" fill={color} />
    <rect x="15" y="10" width="2" height="2" rx="0.4" fill={color} />

    {/* Door */}
    <path
      d="M10 21V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V21"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CafeIcon = ({ size = 16, color = "#10b981" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
    <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
    <line x1="6" y1="1" x2="6" y2="4" />
    <line x1="10" y1="1" x2="10" y2="4" />
    <line x1="14" y1="1" x2="14" y2="4" />
  </svg>
);

const MapPinIcon = ({ size = 16, color = "#94a3af" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const PhoneIcon = ({ size = 16, color = "#94a3af" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ChevronLeftIcon = ({ size = 20, color = "#ffffff" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = ({ size = 20, color = "#ffffff" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const LoadingSpinner = () => (
  <svg
    className="spinner-icon"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="10" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" opacity="1" />
  </svg>
);

const MapSection = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const [hotels, setHotels] = useState([]);
  const [cafes, setCafes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hRes, cRes] = await Promise.all([
          fetch("https://api.moroccool.com/api/hotels"),
          fetch("https://api.moroccool.com/api/cafes"),
        ]);
        const hotelsData = await hRes.json();
        const cafesData = await cRes.json();
        setHotels(Array.isArray(hotelsData) ? hotelsData : []);
        setCafes(Array.isArray(cafesData) ? cafesData : []);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (loading || error || !mapRef.current) return;
    if (mapInstanceRef.current) return;

    const initMap = async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");

      const map = L.map(mapRef.current, {
        zoomControl: true,
        attributionControl: false,
      }).setView([31.7917, -7.0926], 5.5);

      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        { maxZoom: 18 }
      ).addTo(map);

      mapInstanceRef.current = map;

      const allItems = [
        ...hotels.map((h) => ({ ...h, _itemType: "hotel" })),
        ...cafes.map((c) => ({ ...c, _itemType: "cafe" })),
      ];

      let count = 0;
      allItems.forEach((item) => {
        const coords = parseCoords(item.maps);
        if (!coords) return;

        const icon = L.divIcon({
          html: createIconicPin(item._itemType),
          iconSize: [36, 44],
          iconAnchor: [18, 44],
          popupAnchor: [0, -44],
          className: "iconic-marker",
        });

        const marker = L.marker([coords.lat, coords.lng], { icon });
        marker.on("click", () => {
          setSelectedItem(item);
          setCurrentPhotoIndex(0);
        });
        marker.addTo(map);
        markersRef.current.push({ marker, item });
        count++;
      });

      setVisibleCount(count);
    };

    initMap();
  }, [loading, error, hotels, cafes]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const L = window.L;
    if (!L) return;

    let count = 0;
    markersRef.current.forEach(({ marker, item }) => {
      const show = filter === "all" || item._itemType === filter;
      if (show) {
        marker.addTo(mapInstanceRef.current);
        count++;
      } else {
        marker.remove();
      }
    });
    setVisibleCount(count);
  }, [filter]);

  const getCityBreakdown = () => {
    const all = [
      ...hotels.map((h) => ({ ...h, _itemType: "hotel" })),
      ...cafes.map((c) => ({ ...c, _itemType: "cafe" })),
    ].filter((item) => filter === "all" || item._itemType === filter);

    const cities = {};
    all.forEach((item) => {
      const c = (item.city || "Autre").toLowerCase().trim();
      cities[c] = (cities[c] || 0) + 1;
    });
    return Object.entries(cities)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 7);
  };

  const cityData = getCityBreakdown();
  const maxCity = cityData[0]?.[1] || 1;

  const geolocatedHotels = hotels.filter((h) => parseCoords(h.maps)).length;
  const geolocatedCafes = cafes.filter((c) => parseCoords(c.maps)).length;

  // Photo slider logic
  const getPhotos = () => {
    if (!selectedItem) return [];
    const photos = selectedItem.photos || [];
    const logo = selectedItem.logo;
    return logo ? [logo, ...photos] : photos;
  };

  const photos = getPhotos();
  const currentPhoto = photos[currentPhotoIndex] || null;

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="ms-root">
      <div className="ms-header">
        <div>
          <h2 className="ms-title">Carte des listings — Maroc</h2>
          <p className="ms-subtitle">
            Hôtels et Cafés & Restaurants géolocalisés en temps réel
          </p>
        </div>
        <div className="ms-legend">
          <span className="leg-item">
            <span className="leg-dot" style={{ background: HOTEL_COLOR }} />
            Hôtels
          </span>
          <span className="leg-item">
            <span className="leg-dot" style={{ background: CAFE_COLOR }} />
            Cafés & Restaurants
          </span>
        </div>
      </div>

      <div className="ms-filters">
        <span className="filter-label">Afficher :</span>
        {[
          { key: "all", label: "Tous" },
          { key: "hotel", label: "Hôtels" },
          { key: "cafe", label: "Cafés & Restaurants" },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={`f-btn ${filter === key ? "active" : ""}`}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
        <span className="filter-count">
          {visibleCount} listing{visibleCount !== 1 ? "s" : ""} affiché
          {visibleCount !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="ms-body">
        <div className="map-wrap">
          <div className="map-count-badge">
            <span className="badge-hotel">
              <HotelIcon size={14} color="#6366f1" />
              {geolocatedHotels}
            </span>
            <span className="badge-cafe">
              <CafeIcon size={14} color="#10b981" />
              {geolocatedCafes}
            </span>
          </div>

          {loading && (
            <div className="map-overlay">
              <div className="spinner">
                <LoadingSpinner />
              </div>
              <p>Chargement des données…</p>
            </div>
          )}

          {error && (
            <div className="map-overlay">
              <p className="map-error">Impossible de charger l'API</p>
            </div>
          )}

          <div ref={mapRef} className="map-container" />
        </div>

        <div className="ms-sidebar">
          <div className="stats-mini">
            <div className="stat-card">
              <div className="stat-icon-wrapper">
                <HotelIcon size={18} color={HOTEL_COLOR} />
              </div>
              <div className="stat-val" style={{ color: HOTEL_COLOR }}>
                {hotels.length}
              </div>
              <div className="stat-lbl">Hôtels</div>
              <div className="stat-trend">{geolocatedHotels} géolocalisés</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrapper">
                <CafeIcon size={18} color={CAFE_COLOR} />
              </div>
              <div className="stat-val" style={{ color: CAFE_COLOR }}>
                {cafes.length}
              </div>
              <div className="stat-lbl">Cafés & Restaurants</div>
              <div className="stat-trend">{geolocatedCafes} géolocalisés</div>
            </div>
          </div>

          <div className="city-breakdown">
            <div className="cb-title">Top villes</div>
            <div className="cb-list">
              {cityData.length === 0 ? (
                <p className="empty-text">Aucune donnée</p>
              ) : (
                cityData.map(([city, count]) => (
                  <div className="cb-row" key={city}>
                    <span className="cb-name">{capitalize(city)}</span>
                    <div className="cb-bar-wrap">
                      <div
                        className="cb-bar"
                        style={{
                          width: `${Math.round((count / maxCity) * 100)}%`,
                        }}
                      />
                    </div>
                    <span className="cb-count">{count}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="detail-card">
            {!selectedItem ? (
              <div className="no-select-wrapper">
                <MapPinIcon size={20} color="#d1d5db" />
                <p className="no-select">
                  Cliquez sur un pin pour voir les détails
                </p>
              </div>
            ) : (
              <>
                {/* Photo Slider */}
                {photos.length > 0 ? (
                  <div className="photo-slider-container">
                    <div className="photo-slider">
                      <img
                        src={currentPhoto}
                        alt={`${selectedItem.name} - Photo ${currentPhotoIndex + 1}`}
                        className="slider-image"
                      />
                      
                      {photos.length > 1 && (
                        <>
                          <button
                            className="slider-button slider-button-prev"
                            onClick={handlePrevPhoto}
                            aria-label="Photo précédente"
                          >
                            <ChevronLeftIcon />
                          </button>
                          <button
                            className="slider-button slider-button-next"
                            onClick={handleNextPhoto}
                            aria-label="Photo suivante"
                          >
                            <ChevronRightIcon />
                          </button>
                        </>
                      )}

                      {/* Photo indicators */}
                      {photos.length > 1 && (
                        <div className="slider-indicators">
                          {photos.map((_, index) => (
                            <button
                              key={index}
                              className={`indicator ${
                                index === currentPhotoIndex ? "active" : ""
                              }`}
                              onClick={() => setCurrentPhotoIndex(index)}
                              aria-label={`Photo ${index + 1}`}
                            />
                          ))}
                        </div>
                      )}

                      {/* Photo counter */}
                      <div className="photo-counter">
                        {currentPhotoIndex + 1} / {photos.length}
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Property Details */}
                <div className="detail-content">
                  <div className="detail-header">
                    <div>
                      <div className="detail-name">{selectedItem.name}</div>
                      <div className="detail-city">
                        {capitalize(selectedItem.city || "")}
                      </div>
                    </div>
                    <span
                      className={`detail-badge ${
                        selectedItem._itemType === "hotel" ? "badge-h" : "badge-c"
                      }`}
                    >
                      {selectedItem._itemType === "hotel"
                        ? "Hôtel"
                        : "Café / Resto"}
                    </span>
                  </div>

                  {/* Property Info */}
                  <div className="detail-info">
                    {selectedItem.location && (
                      <p className="detail-meta">
                        <MapPinIcon size={14} color="#6b7280" />
                        <span>{selectedItem.location}</span>
                      </p>
                    )}
                    {selectedItem.phone && (
                      <p className="detail-meta">
                        <PhoneIcon size={14} color="#6b7280" />
                        <a href={`tel:${selectedItem.phone}`}>
                          {selectedItem.phone}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapSection;
